import { AudioLoader, MeshBasicMaterial, PositionalAudio, AudioListener, } from "three";
import { Vector3 } from "flavr-math";
import { mainScene } from "@modules/rendering/mainScene";
import { SpeedViolationTracker } from "./SpeedViolationTracker";
const BEEP_DURATION = 0.05; // duration of each beep
const MIN_FREQUENCY = 50; // lowest beep frequency (since a minimal speed of V_MIN_THRESHOLD * threshhold)
const MAX_FREQUENCY = 440; // maximum beep frequency (when near threshhold)
const MAX_INTERVAL = 500; // ms - Interval of beeps is also interpolated with speed
const MIN_INTERVAL = 50; // ms
const V_MIN_THRESHOLD = 0.1; // Factor of threshold at which audio is played
const SPEED_INTERPOLATION_FACTOR = 0.3;
// Helper to map speed to beep frequency and interval
function mapRange(value, inMin, inMax, outMin, outMax) {
    const t = Math.min(Math.max((value - inMin) / (inMax - inMin), 0), 1);
    return outMin + (outMax - outMin) * t;
}
// Buffer for alarm wav
let alarmLoopBuffer;
// load file and assign buffer
const file = "sound/alarm.wav";
const loader = new AudioLoader();
loader.load(file, (buffer) => { alarmLoopBuffer = buffer; });
export class AbstractTrackedObject {
    get violations() {
        return this.violationTracker.violations;
    }
    set violations(violations) {
        this.violationTracker.reset(violations);
    }
    constructor(name, threshold, referencedObjectUid) {
        var _a;
        this.speeds = [];
        this.lastSpeed = 0;
        this.lastBeepAt = 0;
        this.loopSource = undefined;
        this.loopGain = undefined;
        // Speed stats
        this.violationTracker = new SpeedViolationTracker();
        this.history = [];
        this.name = name;
        this.threshold = threshold;
        this.referencedObject = mainScene.getObjectById(referencedObjectUid);
        const listener = new AudioListener();
        (_a = this.referencedObject) === null || _a === void 0 ? void 0 : _a.add(listener);
        // 2. Create a Three.js positional audio object
        this.positionalAudio = new PositionalAudio(listener);
        this.audioListener = listener;
    }
    deactivate() {
        this.violationTracker.endEpisode();
        this.stopAlarmSound();
    }
    /** Whether the tracked object currently delivers valid data (e.g. a controller is connected). */
    isAvailable() {
        return true;
    }
    evaluateSpeedAndUpdateSound(timestamp, activeStage, trackingActive, audioVisualFeedbackActive) {
        var _a, _b;
        if (!this.isAvailable()) {
            this.violationTracker.endEpisode();
            return;
        }
        const currentSpeed = (_b = (_a = this.getLinearVelocity()) === null || _a === void 0 ? void 0 : _a.length()) !== null && _b !== void 0 ? _b : 0;
        const speed = currentSpeed * SPEED_INTERPOLATION_FACTOR + this.lastSpeed * (1 - SPEED_INTERPOLATION_FACTOR);
        this.lastSpeed = speed;
        // Push into history
        if (trackingActive === true) {
            this.speeds.push(speed);
            this.violationTracker.sample(speed, this.threshold, timestamp, activeStage);
            this.history.push({
                timestamp,
                speed,
                stage: activeStage,
                isViolation: speed > this.threshold
            });
        }
        else {
            this.violationTracker.endEpisode(timestamp);
        }
        if (audioVisualFeedbackActive === true) {
            this.updateTransformation();
            this.updateSound(speed);
            this.updateVisualizer(speed);
        }
    }
    /// Stats
    getStats() {
        const arr = this.speeds;
        const count = arr.length;
        if (count === 0)
            return { mean: 0, median: 0, max: 0, count: 0, threshold: this.threshold, violations: [], samples: [] };
        let mean = 0;
        let max = Number.NEGATIVE_INFINITY;
        for (let i = 0; i < arr.length; i++) {
            const v = arr[i];
            // avoid summing up values and divide by count, you will get bad rounding errors, use this instead to calc mean
            mean += (v - mean) / (i + 1);
            if (v > max)
                max = v;
        }
        const sorted = arr.slice().sort((a, b) => a - b);
        const mid = Math.floor(count / 2);
        const median = count % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
        return {
            mean, median, max, count, threshold: this.threshold,
            violations: this.violations.map(violation => (Object.assign({}, violation))),
            samples: this.history.map(sample => (Object.assign({}, sample)))
        };
    }
    /// Positional helper methods ///
    updateTransformation() {
        const position = this.getPosition();
        const orientation = this.getOrientation();
        if (this.positionalAudio !== undefined && position !== undefined) {
            if (this.positionalAudio.parent !== null) {
                this.positionalAudio.parent.position.copy(position);
                this.positionalAudio.parent.quaternion.copy(orientation);
            }
            // Update panner pose
            this.positionalAudio.matrixAutoUpdate = false;
            this.positionalAudio.panner.positionX.value = position.x;
            this.positionalAudio.panner.positionY.value = position.y;
            this.positionalAudio.panner.positionZ.value = position.z;
            const forward = new Vector3(0, 0, -1).applyQuaternion(orientation);
            this.positionalAudio.panner.orientationX.value = forward.x;
            this.positionalAudio.panner.orientationY.value = forward.y;
            this.positionalAudio.panner.orientationZ.value = forward.z;
        }
    }
    /// Audio helper methods ///
    updateSound(speed) {
        if (speed > this.threshold) {
            this.startAlarmSound();
        }
        else {
            this.stopAlarmSound();
            const interval = mapRange(speed, V_MIN_THRESHOLD * this.threshold, this.threshold, MAX_INTERVAL, MIN_INTERVAL);
            const pitch = mapRange(speed, V_MIN_THRESHOLD * this.threshold, this.threshold, MIN_FREQUENCY, MAX_FREQUENCY);
            // Play beep in certain intervals
            const now = Date.now();
            if (now - this.lastBeepAt >= interval && speed > V_MIN_THRESHOLD * this.threshold) {
                this.playBeep(pitch, BEEP_DURATION);
                this.lastBeepAt = now;
            }
        }
    }
    playBeep(pitch, duration) {
        var _a, _b, _c, _d;
        if (this.positionalAudio === undefined)
            return;
        // 3. Create the Web Audio oscillator via the listener context
        const osc = (_a = this.audioListener) === null || _a === void 0 ? void 0 : _a.context.createOscillator();
        const gain = (_b = this.audioListener) === null || _b === void 0 ? void 0 : _b.context.createGain();
        const now = (_d = (_c = this.audioListener) === null || _c === void 0 ? void 0 : _c.context.currentTime) !== null && _d !== void 0 ? _d : 0;
        const fade = 0.005;
        if (osc === undefined || gain === undefined)
            return;
        osc.type = "sine";
        osc.frequency.value = pitch;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(1.0, now + fade);
        gain.gain.setValueAtTime(1.0, now + duration - fade);
        gain.gain.linearRampToValueAtTime(0, now + duration);
        osc.connect(gain);
        gain.connect(this.positionalAudio.panner);
        osc.start(now);
        osc.stop(now + duration + fade);
    }
    startAlarmSound() {
        var _a, _b, _c, _d;
        if (this.loopSource !== undefined || alarmLoopBuffer === undefined || this.positionalAudio === undefined)
            return;
        this.loopSource = (_a = this.audioListener) === null || _a === void 0 ? void 0 : _a.context.createBufferSource();
        this.loopGain = (_b = this.audioListener) === null || _b === void 0 ? void 0 : _b.context.createGain();
        if (this.loopSource === undefined || this.loopGain === undefined)
            return;
        this.loopSource.buffer = alarmLoopBuffer;
        this.loopSource.loop = true;
        this.loopSource.connect(this.loopGain);
        this.loopGain.connect(this.positionalAudio.panner);
        const now = (_d = (_c = this.audioListener) === null || _c === void 0 ? void 0 : _c.context.currentTime) !== null && _d !== void 0 ? _d : 0;
        this.loopGain.gain.setValueAtTime(0, now);
        this.loopGain.gain.linearRampToValueAtTime(1, now + 0.3); // soft fade-in
        this.loopSource.start();
    }
    stopAlarmSound() {
        var _a, _b;
        if (this.loopSource === undefined || this.loopGain === undefined)
            return;
        const now = (_b = (_a = this.audioListener) === null || _a === void 0 ? void 0 : _a.context.currentTime) !== null && _b !== void 0 ? _b : 0;
        this.loopGain.gain.linearRampToValueAtTime(0, now + 0.3); // soft fade-out
        this.loopSource.stop(now + 0.3);
        this.loopSource = undefined;
    }
    /// Visualization helper methods ///
    updateVisualizer(speed) {
        if (this.visualizer === undefined)
            return;
        const radius = mapRange(speed, 0, this.threshold, 0, 0.025);
        const hue = mapRange(speed, 0, this.threshold, 0.33, 0);
        this.visualizer.scale.set(radius, radius, radius);
        if (this.visualizer.material instanceof MeshBasicMaterial)
            this.visualizer.material.color.setHSL(hue, 1, 0.5);
    }
}
